import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useQueryClient} from '@tanstack/react-query'
import {useConfig} from '@salesforce/commerce-sdk-react'
import useMultiSite from '../../../hooks/use-multi-site'
import {useNavigationContent} from '../../../hooks/useScapiCustomApi'
import ModuleContainer from '../../components/module-container'
import FlyoutNavigationLevel1 from '../../components/navigation/flyout/partials/flyout-navigation-level1'
import NavigationPreviewToolNav from './tool-nav'
import {hasNavigationTeaserContent} from '../../components/navigation/navigation-teaser'
import NavigationPreviewPanes from './panes'
import PreviewPane from './pane'
import {PreviewPaneNotices} from './notices'
import {
    headerFlyoutPreviewMessage,
    hiddenLabels,
    level1TeasersMobileOnlyMessage,
    missingLevel2Message,
    multipleLevel1ModulesMessage,
    unwrap
} from './diagnostics'
import {LEVEL1_ANCHOR, jumpTo, level2Anchor} from './anchors'

// the level-1 pane on both viewports — also seeds the cache the header's own
// flyout reads, so the same tree previews there
const NavigationLevel1 = (props) => {
    const {
        moduleId,
        moduleType,
        pageId,
        previewLevel1ModuleCount,
        elements = [],
        teasers = []
    } = props
    const queryClient = useQueryClient()
    const {organizationId} = useConfig()
    const {
        locale: {id: locale}
    } = useMultiSite()

    useEffect(() => {
        const queryKey = useNavigationContent.queryKey({organizationId, locale})
        // a request still in flight would land after the write and replace the preview
        queryClient.cancelQueries({queryKey})
        queryClient.setQueryData(queryKey, props)
    }, [queryClient, organizationId, locale, props])

    // the flyout tree unwraps its entries the same way
    const resolvedElements = useMemo(() => elements.map(unwrap).filter(Boolean), [elements])
    const resolvedTeasers = useMemo(() => teasers.filter(hasNavigationTeaserContent), [teasers])

    // flagged here as well as by the flyout's click-time toast, so finding the
    // broken rows does not mean clicking every one
    const issues = useMemo(
        () => [
            // the page's own fault rather than this payload's, so it leads
            ...(previewLevel1ModuleCount > 1
                ? [multipleLevel1ModulesMessage(previewLevel1ModuleCount)]
                : []),
            ...resolvedElements
                .filter((item) => item.type === 'navigationParentLevel1' && !item.level2Module)
                .map(missingLevel2Message)
        ],
        [previewLevel1ModuleCount, resolvedElements]
    )
    const hidden = useMemo(
        () => hiddenLabels([...resolvedElements, ...resolvedTeasers]),
        [resolvedElements, resolvedTeasers]
    )
    // the flyout note holds for every payload; the teaser note only where the
    // teasers do not show
    const info = useMemo(
        () => [
            {message: headerFlyoutPreviewMessage()},
            ...(resolvedTeasers.length
                ? [{message: level1TeasersMobileOnlyMessage(), viewport: 'desktop'}]
                : [])
        ],
        [resolvedTeasers]
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={LEVEL1_ANCHOR}
            background="none"
        >
            <PreviewPane trail={[{label: 'L1'}]}>
                <PreviewPaneNotices issues={issues} hidden={hidden} info={info} />
                <NavigationPreviewPanes reserve="level2">
                    <FlyoutNavigationLevel1
                        elements={resolvedElements}
                        teasers={resolvedTeasers}
                        onSelect={(id) => jumpTo(level2Anchor(id))}
                    />
                </NavigationPreviewPanes>
                <NavigationPreviewToolNav />
            </PreviewPane>
        </ModuleContainer>
    )
}

NavigationLevel1.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    previewLevel1ModuleCount: PropTypes.number,
    elements: PropTypes.array,
    teasers: PropTypes.array
}

export default NavigationLevel1
